import qapi from "views_shared/app/javascript/api";
import { serializeModel } from "../edit/employment_modal_body/serialization";

// Context-fed action hook for employment — mirrors use_pr_profile_update_api. The provider owns the
// `employments` collection (via the invariant-preserving setEmployments); this performs writes against it
// (optimistic update + snapshot rollback), routes failures to the shared generalError, and reconciles via
// silentReload. The companyJobs mutation client already throws on a non-2xx (throwOnApiError), so these
// try/catch blocks are load-bearing.
const usePrEmploymentsApi = ({
  prProfileId,
  employments,
  setEmployments,
  silentReload,
  setGeneralError,
  setIsUpdating,
  isUpdating,
}) => {
  const addEmployment = async (employment) => {
    if (isUpdating) return; // guard overlapping mutations
    setGeneralError(null);
    setIsUpdating(true);
    try {
      const { id: _removed, ...serialized } = serializeModel(employment);
      // A reload (not a manual optimistic insert) is simplest here — the new row only has a server id
      // after the create, and silentReload re-orders it correctly via the provider fetch.
      await qapi.companyJobs.createPrProfileJob(employment.companyId, prProfileId, serialized);
      await silentReload();
    } catch (e) {
      setGeneralError(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const removeEmployment = async (id) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = employments;

    setEmployments((current) => current.filter((p) => p.id !== id)); // optimistic

    setIsUpdating(true);
    try {
      await qapi.companyJobs.deletePrProfileJobById(prProfileId, id);
      await silentReload();
    } catch (e) {
      setEmployments(snapshot); // roll back
      setGeneralError(e);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateEmployment = async (id, employment) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = employments;

    setEmployments((current) => current.map((e) => (e.id === id ? { ...e, ...employment } : e))); // optimistic

    setIsUpdating(true);
    try {
      const { id: _id, ...attributes } = employment;
      await qapi.companyJobs.updateByPersonIdAndId(prProfileId, id, serializeModel(attributes));
      await silentReload();
    } catch (e) {
      setEmployments(snapshot); // roll back
      setGeneralError(e);
    } finally {
      setIsUpdating(false);
    }
  };

  // person_featured is flip-only (never writable via update), so this goes through the dedicated
  // feature/unfeature endpoints. Featuring clears any sibling star server-side (one featured job per
  // person), so the optimistic update mirrors that; the reload then re-orders the collection and
  // refreshes the header title, which derives from the featured job.
  const setFeatured = async (id, featured) => {
    if (isUpdating) return;
    setGeneralError(null);
    const snapshot = employments;

    setEmployments((current) =>
      current.map((e) => {
        if (e.id === id) return { ...e, featured };
        return featured ? { ...e, featured: false } : e;
      }),
    ); // optimistic

    setIsUpdating(true);
    try {
      await qapi.companyJobs.setPrProfileJobFeaturedById(prProfileId, id, featured);
      await silentReload();
    } catch (e) {
      setEmployments(snapshot); // roll back
      setGeneralError(e);
    } finally {
      setIsUpdating(false);
    }
  };

  return { addEmployment, removeEmployment, setFeatured, updateEmployment };
};

export default usePrEmploymentsApi;
